/**
 * "Is this the same book?" — one rule, because the loose version of this
 * question is asked all over the site and the strict version silently fails.
 *
 * The weekly newsletter links each recommendation to
 * `/library?add=<title>&author=<author>`, and the handler that received it
 * compared the author with `===`. Exact string equality on a freehand author
 * column does not survive contact with this catalogue: it holds "Edwin Abbott"
 * and "Edwin A. Abbott" for the same man, "H.G. Wells" and "H. G. Wells" for
 * the same one, and the newsletter's own spelling need not match either. A miss
 * did not look like a miss — it fell through to "not in your library" and
 * offered to add a duplicate.
 */

/**
 * Case, accents and punctuation folded, so a full stop cannot hide a match.
 *
 * `\p{L}\p{N}` and NOT `a-z0-9`: the ASCII class deleted every character of a
 * title written in any non-Latin script, so 三体 and Мы folded to the EMPTY
 * STRING. isSameBook then failed its `!ta` guard and answered "different book"
 * for a title against itself — which is precisely the miss described above,
 * falling through to "not in your library" and offering to add a duplicate.
 * pickBestBookMatch returned null for the same reason, so the Apple Books
 * edition, price and buy link vanished from the preview.
 *
 * Nothing in the catalogue tripped this when it was found (0 of 148 titles held
 * a non-ASCII character), but `transmissions` is a per-user shelf fed by
 * Google Books search, so it would have fired on the first reader to add a book
 * under its original title.
 */
export const foldText = (s?: string | null): string =>
  (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Words worth matching on — initials and "the" identify nobody. */
const significantWords = (s?: string | null): string[] =>
  foldText(s).split(' ').filter((w) => w.length > 2);

/**
 * Same title, and an author with at least one substantial name in common.
 *
 * The title must match exactly once folded — loosening that pulls in sequels
 * and companion volumes ("Hyperion" vs "The Fall of Hyperion"). The AUTHOR is
 * the part that needs slack, because that is the column people spell freehand.
 *
 * An author is compared only when both sides supply one: the newsletter always
 * does, but other callers may not, and demanding a match against an empty
 * string is what made the original check impossible to satisfy.
 */
export function isSameBook(
  a: { title?: string | null; author?: string | null },
  b: { title?: string | null; author?: string | null },
): boolean {
  const ta = foldText(a.title);
  const tb = foldText(b.title);
  if (!ta || ta !== tb) return false;

  const wa = significantWords(a.author);
  const wb = significantWords(b.author);
  if (!wa.length || !wb.length) return true; // title agreed and nobody claimed an author

  return wa.some((w) => wb.includes(w));
}

/**
 * Title folding for CATALOGUE LOOKUPS, where a bookseller's title is never
 * going to be character-identical to ours. Stop-words dropped as well as
 * punctuation, because "The War of the Worlds" and "War of the Worlds" are one
 * book. Deliberately looser than foldText, which is used where exactness
 * matters.
 */
const foldLookupTitle = (s?: string | null) =>
  (s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N} ]/gu, ' ').replace(/\b(the|a|an|and|of|or)\b/g, ' ')
    .replace(/\s+/g, ' ').trim();

/**
 * Is this search result plausibly the book we asked for?
 *
 * Substring either way, or 70% of the wanted title's words present. Moved here
 * from FilmBookCover, where it was gating cover lookups while the Apple Books
 * search had no equivalent check at all and simply took results[0].
 */
export function isConfidentTitleMatch(candidate?: string | null, wanted?: string | null): boolean {
  const c = foldLookupTitle(candidate);
  const b = foldLookupTitle(wanted);
  if (!c || !b) return false;
  if (c.includes(b) || b.includes(c)) return true;
  const cw = new Set(c.split(' '));
  const bw = b.split(' ').filter(Boolean);
  const hits = bw.filter((w) => cw.has(w)).length;
  return bw.length > 0 && hits / bw.length >= 0.7;
}

/**
 * Editions that are not the book, however well the title matches.
 *
 * Apple returns "Philip K. Dick's Vulcan's Hammer - Unabridged" by "Philip K.
 * Dick & Kevin Theis" for Vulcan's Hammer, and labels it kind='ebook'. The
 * title check passes it — the candidate genuinely contains the book's title —
 * and the author check passes it too, because Dick is one of the two names.
 * It is a narrated edition with a second byline, and buying it does not get
 * you the novel.
 *
 * Only rejected when WE did not ask for it: a shelf that really does hold
 * "Dune: The Graphic Novel" still matches its own edition.
 */
const EDITION_MARKERS = /\b(un)?abridged\b|\baudiobook\b|\bgraphic novel\b|\baudio edition\b|\bboxed set\b|\bbox set\b/i;

export function isSameEdition(candidate?: string | null, wanted?: string | null): boolean {
  const c = candidate ?? '';
  const w = wanted ?? '';
  return !(EDITION_MARKERS.test(c) && !EDITION_MARKERS.test(w));
}

/**
 * The best result for a title/author lookup, or null when nothing is good
 * enough. Returning null is a real answer: the caller falls back to another
 * source, which is better than confidently showing the wrong book.
 */
export function pickBestBookMatch<T extends { title?: string | null; author?: string | null }>(
  results: T[],
  wantedTitle: string,
  wantedAuthor: string,
): T | null {
  const viable = results.filter(
    (r) =>
      isConfidentTitleMatch(r.title, wantedTitle) &&
      isSameEdition(r.title, wantedTitle) &&
      isSameBook({ title: r.title, author: r.author }, { title: r.title, author: wantedAuthor }),
  );
  if (!viable.length) return null;

  // An exact title beats one that merely contains it, so the plain novel wins
  // over "<Author>'s <Title>" tie-ins when both are on offer.
  const exact = viable.find((r) => foldLookupTitle(r.title) === foldLookupTitle(wantedTitle));
  return exact ?? viable[0];
}
